import { useState, useRef, useCallback, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Book,
  BookOpen,
  Calculator,
  Tag,
  Building2,
  CalendarDays,
  MoreHorizontal,
  X,
  ChevronRight,
  LogOut,
  User,
  Palette,
  Lock,
  Bell,
  BarChart3,
  SlidersHorizontal,
  Download,
  MessageSquare,
  Send,
  Loader2,
  ShieldCheck,
  Repeat,
  Sparkles,
  KeyRound,
  ArrowDownLeft,
  ArrowUpRight,
  Users,
  Gift,
  LineChart,
  Crown,
  ListChecks,
  Wallet,
  FileText
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { usePWA } from '../../context/PWAContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { feedbackAPI } from '../../services/api';
import { exitApp, isNative, isIOS } from '../../services/native';
import useModalBack from '../../hooks/useModalBack';
import { useNotificationUnreadCountQuery } from '../../hooks/useQueries';

// Admin bottom nav items
const adminNavItems = [
  { path: '/admin?tab=dashboard', icon: BarChart3, label: 'Home' },
  { path: '/admin?tab=users', icon: Users, label: 'Users' },
  { path: '/admin?tab=feedback', icon: MessageSquare, label: 'Feedback' },
  { path: '/admin?tab=subscriptions', icon: Crown, label: 'Subs' },
];

// Admin navigation order for swipe
const ADMIN_NAV_ORDER = ['/admin?tab=dashboard', '/admin?tab=users', '/admin?tab=feedback', '/admin?tab=subscriptions'];

// Nav items (order: Home, Stats, Accounts, Calendar, More)
const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Home' },
  { path: '/stats', icon: BarChart3, label: 'Stats' },
  { path: '/accounts', icon: Building2, label: 'Accounts' },
  { path: '/calendar', icon: CalendarDays, label: 'Calendar' },
];

// Navigation order for swipe
const NAV_ORDER = ['/', '/stats', '/accounts', '/calendar'];

// Admin More menu items (flat list)
const getAdminMoreMenuItems = () => [
  { path: '/admin?tab=dashboard', emoji: '📊', icon: BarChart3, label: 'Dashboard', description: 'Admin overview' },
  { path: '/admin?tab=users', emoji: '👥', icon: Users, label: 'Users', description: 'Manage users' },
  { path: '/admin?tab=leads', emoji: '📧', icon: Send, label: 'Leads', description: 'Marketing leads' },
  { path: '/admin?tab=feedback', emoji: '💬', icon: MessageSquare, label: 'Feedback', description: 'User feedback' },
  { path: '/admin?tab=reports', emoji: '🛡️', icon: ShieldCheck, label: 'Reports', description: 'User reports' },
  { path: '/admin?tab=coupons', emoji: '🏷️', icon: Tag, label: 'Coupons', description: 'Manage coupons' },
  { path: '/admin?tab=plans', emoji: '⚙️', icon: SlidersHorizontal, label: 'Plans', description: 'Plan config' },
  { path: '/admin?tab=subscriptions', emoji: '👑', icon: Crown, label: 'Subscriptions', description: 'User subscriptions' },
  { path: '/admin?tab=settlements', emoji: '💰', icon: Wallet, label: 'Settlements', description: 'Razorpay settlements' },
  { path: '/admin?tab=notifications', emoji: '🔔', icon: Bell, label: 'Notifications', description: 'Push notifications' },
  { path: '/admin?tab=ai-config', emoji: '✨', icon: Sparkles, label: 'AI Config', description: 'AI features config' },
  { path: '/admin?tab=referrals', emoji: '🎁', icon: Gift, label: 'Referrals', description: 'Referral program' },
  { path: '/admin?tab=blog', emoji: '📝', icon: FileText, label: 'Blog', description: 'Manage blog posts' },
];

// Get all menu items for the More screen (flat list for classic UI)
const getMoreMenuItems = (isAdmin) => {
  if (isAdmin) return getAdminMoreMenuItems();

  const items = [
    { path: '/', emoji: '🏠', icon: LayoutDashboard, label: 'Dashboard', description: 'Overview & summary' },
    { path: '/transactions', emoji: '📒', icon: Book, label: 'Transactions', description: 'Income & expenses' },
    { path: '/recurring', emoji: '🔁', icon: Repeat, label: 'Recurring', description: 'Scheduled transactions' },
    { path: '/receivables', emoji: '💵', icon: ArrowDownLeft, label: 'Receivables', description: 'Money to receive' },
    { path: '/debts', emoji: '🏦', icon: ArrowUpRight, label: 'Debts', description: 'Money to pay' },
    { path: '/contacts', emoji: '👥', icon: Users, label: 'Contacts', description: 'Manage contacts' },
    { path: '/checklist', emoji: '✅', icon: ListChecks, label: 'Checklist', description: 'Monthly bills & tasks' },
    { path: '/calendar', emoji: '📅', icon: CalendarDays, label: 'Calendar', description: 'Monthly view' },
    { path: '/accounts', emoji: '🏦', icon: Building2, label: 'Accounts', description: 'Bank accounts' },
    { path: '/categories', emoji: '🏷️', icon: Tag, label: 'Categories', description: 'Manage categories' },
    { path: '/stats', emoji: '📊', icon: BarChart3, label: 'Stats', description: 'Category insights' },
    { path: '/trend', emoji: '📈', icon: LineChart, label: 'Trend', description: 'Income vs expense trend' },
    { path: '/ledger', emoji: '📖', icon: BookOpen, label: 'Ledger', description: 'Export transactions' },
    { path: '/insights', emoji: '✨', icon: Sparkles, label: 'HJ Insights', description: 'Hisab Journey Insights' },
  ];

  return items;
};

// Admin grouped menu items for modern UI
const getAdminGroupedMenuItems = () => [
  {
    id: 'admin-overview',
    label: 'Overview',
    items: [
      { path: '/admin?tab=dashboard', emoji: '📊', label: 'Dashboard' },
      { path: '/admin?tab=users', emoji: '👥', label: 'Users' },
      { path: '/admin?tab=leads', emoji: '📧', label: 'Leads' },
    ]
  },
  {
    id: 'admin-manage',
    label: 'Management',
    items: [
      { path: '/admin?tab=feedback', emoji: '💬', label: 'Feedback' },
      { path: '/admin?tab=reports', emoji: '🛡️', label: 'Reports' },
      { path: '/admin?tab=coupons', emoji: '🏷️', label: 'Coupons' },
      { path: '/admin?tab=subscriptions', emoji: '👑', label: 'Subscriptions' },
      { path: '/admin?tab=settlements', emoji: '💰', label: 'Settlements' },
    ]
  },
  {
    id: 'admin-config',
    label: 'Configuration',
    items: [
      { path: '/admin?tab=plans', emoji: '⚙️', label: 'Plans' },
      { path: '/admin?tab=notifications', emoji: '🔔', label: 'Notifications' },
      { path: '/admin?tab=ai-config', emoji: '✨', label: 'AI Config' },
      { path: '/admin?tab=referrals', emoji: '🎁', label: 'Referrals' },
      { path: '/admin?tab=blog', emoji: '📝', label: 'Blog' },
    ]
  },
];

// Get grouped menu items for modern UI
const getGroupedMenuItems = (isAdmin) => {
  if (isAdmin) return getAdminGroupedMenuItems();

  const groups = [
    {
      id: 'overview',
      label: 'Overview',
      items: [
        { path: '/', emoji: '🏠', label: 'Dashboard' },
        { path: '/calendar', emoji: '📅', label: 'Calendar' },
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      items: [
        { path: '/transactions', emoji: '📒', label: 'Transactions' },
        { path: '/recurring', emoji: '🔁', label: 'Recurring' },
        { path: '/accounts', emoji: '🏦', label: 'Accounts' },
      ]
    },
    {
      id: 'tracking',
      label: 'Tracking',
      items: [
        { path: '/receivables', emoji: '💵', label: 'Receivables' },
        { path: '/debts', emoji: '🏦', label: 'Debts' },
        { path: '/checklist', emoji: '✅', label: 'Checklist' },
        { path: '/contacts', emoji: '👥', label: 'Contacts' },
      ]
    },
    {
      id: 'insights',
      label: 'Analytics',
      items: [
        { path: '/stats', emoji: '📊', label: 'Stats' },
        { path: '/trend', emoji: '📈', label: 'Trend' },
        { path: '/ledger', emoji: '📖', label: 'Ledger' },
        { path: '/insights', emoji: '✨', label: 'HJ Insights' },
        { path: '/categories', emoji: '🏷️', label: 'Categories' },
      ]
    },
  ];

  return groups;
};

// Color classes for settings items (static strings for Tailwind JIT)
const SETTINGS_COLORS = {
  teal: {
    modernBtn: 'border-teal-100 hover:border-teal-200 hover:shadow-lg hover:shadow-teal-100',
    classicBtn: 'bg-teal-50 hover:bg-teal-100',
    iconBox: 'from-teal-100 to-teal-200',
    classicIconBox: 'bg-teal-100',
    icon: 'text-teal-600',
    label: 'text-teal-700',
  },
  blue: {
    modernBtn: 'border-blue-100 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100',
    classicBtn: 'bg-blue-50 hover:bg-blue-100',
    iconBox: 'from-blue-100 to-blue-200',
    classicIconBox: 'bg-blue-100',
    icon: 'text-blue-600',
    label: 'text-blue-700',
  },
  pink: {
    modernBtn: 'border-pink-100 hover:border-pink-200 hover:shadow-lg hover:shadow-pink-100',
    classicBtn: 'bg-pink-50 hover:bg-pink-100',
    iconBox: 'from-pink-100 to-pink-200',
    classicIconBox: 'bg-pink-100',
    icon: 'text-pink-600',
    label: 'text-pink-700',
  },
  orange: {
    modernBtn: 'border-orange-100 hover:border-orange-200 hover:shadow-lg hover:shadow-orange-100',
    classicBtn: 'bg-orange-50 hover:bg-orange-100',
    iconBox: 'from-orange-100 to-orange-200',
    classicIconBox: 'bg-orange-100',
    icon: 'text-orange-600',
    label: 'text-orange-700',
  },
  red: {
    modernBtn: 'border-red-100 hover:border-red-200 hover:shadow-lg hover:shadow-red-100',
    classicBtn: 'bg-red-50 hover:bg-red-100',
    iconBox: 'from-red-100 to-red-200',
    classicIconBox: 'bg-red-100',
    icon: 'text-red-600',
    label: 'text-red-700',
  },
};

// Settings menu items for mobile
const settingsMenuItems = [
  { path: '/configuration', emoji: '⚙️', icon: SlidersHorizontal, label: 'Configuration', description: 'Currency, date & more', color: 'teal' },
  { path: '/settings/profile', emoji: '👤', icon: User, label: 'Profile', description: 'Account settings', color: 'blue' },
  { path: '/settings/theme', emoji: '🎨', icon: Palette, label: 'Theme', description: 'Theme & colors', color: 'pink' },
  { path: '/settings/notifications', emoji: '🔔', icon: Bell, label: 'Notifications', description: 'Summary & alerts', color: 'orange' },
  { path: '/settings/security', emoji: '🔐', icon: Lock, label: 'Security', description: 'Password & encryption', color: 'red' },
];

export default function BottomNav() {
  const { user, logout } = useAuth();
  const { isTrialing, isActive, isExpired, isPaused, isCancelled, freeAccessMode, referralEnabled, hidePaymentUI } = useSubscription();
  const { canInstall, installPWA } = usePWA();
  const { bottomNavStyle, isModernUI } = useTheme();
  const showSubscription = !hidePaymentUI && !freeAccessMode && (!isActive || isTrialing);
  const { toast } = useToast();
  const { data: unreadCount = 0 } = useNotificationUnreadCountQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [menuDragY, setMenuDragY] = useState(0);
  const [isMenuDragging, setIsMenuDragging] = useState(false);
  const menuStartY = useRef(0);
  const menuContentRef = useRef(null);

  // Back navigation: track tab history and handle More menu
  const tabHistoryRef = useRef(['/']);
  const backPressTimerRef = useRef(null);
  const isMoreOpenRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isMoreOpenRef.current = isMoreOpen;
  }, [isMoreOpen]);

  // Track tab navigation history (only main nav tabs)
  useEffect(() => {
    if (NAV_ORDER.includes(location.pathname)) {
      const history = tabHistoryRef.current;
      // Don't add duplicate consecutive entries
      if (history[history.length - 1] !== location.pathname) {
        tabHistoryRef.current = [...history, location.pathname];
      }
    }
  }, [location.pathname]);

  // More menu and feedback modal — back button via modalManager
  const openMoreMenu = useCallback(() => setIsMoreOpen(true), []);
  const closeMoreMenu = useCallback(() => setIsMoreOpen(false), []);

  const { closeSilent: closeMoreSilent } = useModalBack(isMoreOpen, closeMoreMenu, 'MoreMenu');

  // Handle Android hardware back button via custom event from native.js
  // Only handles tab navigation and exit — modals are handled by modalManager via native.js
  useEffect(() => {
    const handleBackButton = (e) => {
      // 1. If on a main tab, go to previous tab in history
      const history = tabHistoryRef.current;
      if (NAV_ORDER.includes(location.pathname) && history.length > 1) {
        e.preventDefault();
        const newHistory = history.slice(0, -1);
        tabHistoryRef.current = newHistory;
        const previousTab = newHistory[newHistory.length - 1];
        navigate(previousTab, { replace: true });
        return;
      }

      // 2. At Home (or only one tab in history) — show exit toast
      if (location.pathname === '/' || (NAV_ORDER.includes(location.pathname) && history.length <= 1)) {
        e.preventDefault();
        const now = Date.now();
        if (backPressTimerRef.current && (now - backPressTimerRef.current) < 2000) {
          if (isNative) {
            exitApp();
          }
          return;
        }
        backPressTimerRef.current = now;
        toast.info('Press back again to exit', 2000);
        return;
      }

      // 3. For non-tab pages (e.g. /transactions, /settings), let default back handle it
    };

    window.addEventListener('app-back-button', handleBackButton);
    return () => window.removeEventListener('app-back-button', handleBackButton);
  }, [location.pathname, navigate, toast]);

  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const feedbackModalOpenRef = useRef(false);
  useModalBack(showFeedbackModal, () => setShowFeedbackModal(false), 'FeedbackModal');
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackType, setFeedbackType] = useState('bug');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const baseViewportHeight = useRef(window.visualViewport?.height || window.innerHeight);

  // Detect keyboard open/close using focus events + viewport height
  useEffect(() => {
    const isInputElement = (el) =>
      el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

    const checkKeyboard = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const isSmall = viewportHeight < baseViewportHeight.current * 0.75;
      const modalOpen = document.querySelector('[data-modal="true"]') !== null;
      setIsKeyboardOpen(isSmall && !modalOpen);
    };

    const handleFocusIn = (e) => {
      if (isInputElement(e.target)) {
        // Delay to allow keyboard animation to resize viewport
        setTimeout(checkKeyboard, 300);
      }
    };

    const handleFocusOut = () => {
      // Always show nav when input loses focus
      setIsKeyboardOpen(false);
    };

    const handleResize = () => {
      // Update base height only when no input is focused
      if (!isInputElement(document.activeElement)) {
        baseViewportHeight.current = window.visualViewport?.height || window.innerHeight;
        setIsKeyboardOpen(false);
      } else {
        checkKeyboard();
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  // Listen for close-more-menu event (from pull-to-refresh)
  useEffect(() => {
    const handleCloseMenu = () => closeMoreMenu();
    window.addEventListener('close-more-menu', handleCloseMenu);
    return () => window.removeEventListener('close-more-menu', handleCloseMenu);
  }, [closeMoreMenu]);

  // Reset drag state when menu closes and prevent body scroll when menu is open
  useEffect(() => {
    if (!isMoreOpen) {
      setMenuDragY(0);
      setIsMenuDragging(false);
    }

  }, [isMoreOpen]);

  // Menu drag handlers for smooth close
  const handleMenuTouchStart = useCallback((e) => {
    const scrollTop = menuContentRef.current?.scrollTop || 0;
    // Only allow drag when at top of scroll
    if (scrollTop <= 0) {
      menuStartY.current = e.touches[0].clientY;
      setIsMenuDragging(true);
    }
  }, []);

  const handleMenuTouchMove = useCallback((e) => {
    if (!isMenuDragging) return;
    const scrollTop = menuContentRef.current?.scrollTop || 0;

    // If user scrolled down, cancel drag
    if (scrollTop > 0) {
      setIsMenuDragging(false);
      setMenuDragY(0);
      return;
    }

    const currentY = e.touches[0].clientY;
    const diff = currentY - menuStartY.current;

    // Only allow downward drag with smooth resistance
    if (diff > 0) {
      // Apply resistance - the further you pull, the harder it gets (like native apps)
      const resistance = 0.6;
      const smoothDrag = diff * resistance;
      setMenuDragY(smoothDrag);
      e.preventDefault();
    }
  }, [isMenuDragging]);

  const handleMenuTouchEnd = useCallback(() => {
    if (!isMenuDragging) return;

    // Close if dragged more than 100px (accounting for resistance)
    if (menuDragY > 100) {
      closeMoreMenu();
    }

    setMenuDragY(0);
    setIsMenuDragging(false);
  }, [isMenuDragging, menuDragY, closeMoreMenu]);

  // Handle feedback submission
  const handleFeedbackSubmit = async () => {
    if (!feedbackTitle.trim() || !feedbackMessage.trim()) {
      setFeedbackError('Please fill in both title and message');
      return;
    }

    setFeedbackSubmitting(true);
    setFeedbackError('');

    try {
      await feedbackAPI.create({
        title: feedbackTitle.trim(),
        message: feedbackMessage.trim(),
        type: feedbackType
      });
      setFeedbackSuccess(true);
      setFeedbackTitle('');
      setFeedbackMessage('');
      // Close modal after 2 seconds
      setTimeout(() => {
        if (feedbackModalOpenRef.current) {
          setShowFeedbackModal(false);
          setFeedbackSuccess(false);
        }
      }, 2000);
    } catch (err) {
      setFeedbackError(err.error || 'Failed to submit feedback');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Keep feedback modal ref in sync
  useEffect(() => {
    feedbackModalOpenRef.current = showFeedbackModal;
  }, [showFeedbackModal]);

  // Open feedback modal
  const openFeedbackModal = () => {
    setShowFeedbackModal(true);
    setFeedbackError('');
    setFeedbackSuccess(false);
  };

  // Close feedback modal
  const closeFeedbackModal = useCallback(() => {
    setShowFeedbackModal(false);
  }, []);

  // Swipe handling
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const navRef = useRef(null);
  const minSwipeDistance = 50;

  const isAdmin = user?.isAdmin;
  const activeNavItems = isAdmin ? adminNavItems : navItems;
  const activeNavOrder = isAdmin ? ADMIN_NAV_ORDER : NAV_ORDER;

  // For admin, match by full path+query; for regular, match by pathname
  const currentIndex = isAdmin
    ? activeNavOrder.indexOf(`${location.pathname}${location.search}`)
    : activeNavOrder.indexOf(location.pathname);

  const handleMoreItemClick = (path) => {
    // Close silently (no history.back) then navigate — replace to overwrite the modal history entry
    closeMoreSilent();
    navigate(path, { replace: true });
  };

  const handleLogout = () => {
    closeMoreSilent();
    logout(); // This already redirects to /login
  };

  // Handle swipe start
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  // Handle swipe move
  const handleTouchMove = useCallback((e) => {
    if (!touchStartX.current) return;
    touchEndX.current = e.touches[0].clientX;
  }, []);

  // Handle swipe end
  const handleTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < activeNavOrder.length - 1) {
      navigate(activeNavOrder[currentIndex + 1]);
    } else if (isRightSwipe && currentIndex > 0) {
      navigate(activeNavOrder[currentIndex - 1]);
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [currentIndex, navigate]);

  // All nav items for rendering
  const allNavItems = activeNavItems;

  // Render Classic Style - Labels always visible, white background with border
  const renderClassicNav = () => (
    <nav
      ref={navRef}
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-surface-200 z-50 xl:hidden safe-area-bottom-flush"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-onboarding="swipe-nav"
    >
      <div className="flex justify-around items-center max-w-lg mx-auto py-2 px-1">
        {allNavItems.map((item) => {
          const isItemActive = item.path.includes('?')
            ? `${location.pathname}${location.search}` === item.path || (item.path === '/admin?tab=dashboard' && location.pathname === '/admin' && !location.search)
            : location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center p-1 flex-1 min-w-0 transition-all"
            >
              <item.icon
                className="w-5 h-5 mb-0.5"
                style={{ color: isItemActive ? 'var(--accent-bg)' : '#6b7280' }}
              />
              <span
                className="text-[9px] font-medium truncate max-w-full"
                style={{ color: isItemActive ? 'var(--accent-bg)' : '#6b7280' }}
              >
                {item.label}
              </span>
              {isItemActive && <div className="w-1 h-1 rounded-full mt-0.5" style={{ backgroundColor: 'var(--accent-bg)' }} />}
            </NavLink>
          );
        })}
        {/* More Button */}
        <button
          onClick={openMoreMenu}
          className="flex flex-col items-center justify-center p-1 flex-1 min-w-0 transition-all relative"
          data-onboarding="more-menu"
        >
          <MoreHorizontal
            className="w-5 h-5 mb-0.5"
            style={{ color: isMoreOpen ? 'var(--accent-bg)' : '#6b7280' }}
          />
          <span
            className="text-[9px] font-medium"
            style={{ color: isMoreOpen ? 'var(--accent-bg)' : '#6b7280' }}
          >
            More
          </span>
          {unreadCount > 0 && <div className="absolute top-0.5 right-2 w-2 h-2 bg-expense-500 rounded-full" />}
        </button>
      </div>
    </nav>
  );

  // Render Pill Style - Active item shows pill with label, white background
  const renderPillNav = () => (
    <nav
      ref={navRef}
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-surface-200 z-50 xl:hidden safe-area-bottom-flush"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-onboarding="swipe-nav"
    >
      <div className="flex justify-around items-center max-w-lg mx-auto py-2 px-2">
        {allNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`flex items-center justify-center transition-all ${
                isActive
                  ? 'rounded-full px-3 py-1.5 gap-1.5'
                  : 'p-2'
              }`}
              style={isActive ? { backgroundColor: 'var(--accent-bg)' } : {}}
            >
              <item.icon
                className="w-5 h-5"
                style={{ color: isActive ? 'var(--accent-text)' : '#6b7280' }}
              />
              {isActive && (
                <span
                  className="text-xs font-semibold"
                  style={{ color: 'var(--accent-text)' }}
                >
                  {item.label}
                </span>
              )}
            </NavLink>
          );
        })}
        {/* More Button */}
        <button
          onClick={openMoreMenu}
          className={`flex items-center justify-center transition-all relative ${
            isMoreOpen
              ? 'rounded-full px-3 py-1.5 gap-1.5'
              : 'p-2'
          }`}
          style={isMoreOpen ? { backgroundColor: 'var(--accent-bg)' } : {}}
          data-onboarding="more-menu"
        >
          <MoreHorizontal
            className="w-5 h-5"
            style={{ color: isMoreOpen ? 'var(--accent-text)' : '#6b7280' }}
          />
          {isMoreOpen && (
            <span
              className="text-xs font-semibold"
              style={{ color: 'var(--accent-text)' }}
            >
              More
            </span>
          )}
          {unreadCount > 0 && <div className="absolute top-0 right-0 w-2 h-2 bg-expense-500 rounded-full" />}
        </button>
      </div>
    </nav>
  );

  // Render Floating Pill Style - Dark floating bar with pill active state (Modern)
  const renderFloatingNav = () => (
    <nav
      ref={navRef}
      className="fixed bottom-0 left-0 right-0 px-4 pb-2 z-50 xl:hidden safe-area-bottom"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-onboarding="swipe-nav"
    >
      <div
        className="flex justify-around items-center max-w-lg mx-auto relative py-2.5 px-3 rounded-3xl border border-white/10"
        style={{
          backgroundColor: 'var(--sidebar-bg)',
          boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset'
        }}
      >
        {allNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`flex items-center justify-center transition-all duration-300 active:scale-90 ${
                isActive
                  ? 'rounded-2xl px-4 py-2 gap-2'
                  : 'p-2.5 hover:bg-white/5 rounded-xl'
              }`}
              style={isActive ? {
                backgroundColor: 'var(--accent-bg)',
                boxShadow: '0 4px 12px -2px color-mix(in srgb, var(--accent-bg) 50%, transparent)'
              } : {}}
            >
              <item.icon
                className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}
                style={{ color: isActive ? 'var(--accent-text)' : 'var(--sidebar-text)' }}
              />
              {isActive && (
                <span
                  className="text-xs font-bold tracking-wide"
                  style={{ color: 'var(--accent-text)' }}
                >
                  {item.label}
                </span>
              )}
            </NavLink>
          );
        })}
        {/* More Button */}
        <button
          onClick={openMoreMenu}
          className={`flex items-center justify-center transition-all duration-300 active:scale-90 relative ${
            isMoreOpen
              ? 'rounded-2xl px-4 py-2 gap-2'
              : 'p-2.5 hover:bg-white/5 rounded-xl'
          }`}
          style={isMoreOpen ? {
            backgroundColor: 'var(--accent-bg)',
            boxShadow: '0 4px 12px -2px color-mix(in srgb, var(--accent-bg) 50%, transparent)'
          } : {}}
          data-onboarding="more-menu"
        >
          <MoreHorizontal
            className={`w-5 h-5 transition-transform duration-300 ${isMoreOpen ? 'scale-110' : ''}`}
            style={{ color: isMoreOpen ? 'var(--accent-text)' : 'var(--sidebar-text)' }}
          />
          {isMoreOpen && (
            <span
              className="text-xs font-bold tracking-wide"
              style={{ color: 'var(--accent-text)' }}
            >
              More
            </span>
          )}
          {unreadCount > 0 && !isMoreOpen && <div className="absolute top-1 right-1 w-2 h-2 bg-expense-500 rounded-full border border-[var(--sidebar-bg)]" />}
        </button>
      </div>
    </nav>
  );

  // Render Floating Classic Style - Dark floating bar with labels always visible
  const renderFloatingClassicNav = () => (
    <nav
      ref={navRef}
      className="fixed bottom-0 left-0 right-0 px-3 pb-1 z-50 xl:hidden safe-area-bottom"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-onboarding="swipe-nav"
    >
      <div
        className="flex justify-around items-center max-w-lg mx-auto relative py-2 px-2 rounded-2xl shadow-lg"
        style={{ backgroundColor: 'var(--sidebar-bg)' }}
      >
        {allNavItems.map((item) => {
          const isItemActive = item.path.includes('?')
            ? `${location.pathname}${location.search}` === item.path || (item.path === '/admin?tab=dashboard' && location.pathname === '/admin' && !location.search)
            : location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center p-1.5 min-w-[50px] transition-all"
            >
              <item.icon
                className="w-5 h-5 mb-0.5"
                style={{ color: isItemActive ? 'var(--accent-bg)' : 'var(--sidebar-text)' }}
              />
              <span
                className="text-[10px] font-medium"
                style={{ color: isItemActive ? 'var(--accent-bg)' : 'var(--sidebar-text)' }}
              >
                {item.label}
              </span>
              {isItemActive && <div className="w-1 h-1 rounded-full mt-0.5" style={{ backgroundColor: 'var(--accent-bg)' }} />}
            </NavLink>
          );
        })}
        {/* More Button */}
        <button
          onClick={openMoreMenu}
          className="flex flex-col items-center justify-center p-1.5 min-w-[50px] transition-all relative"
          data-onboarding="more-menu"
        >
          <MoreHorizontal
            className="w-5 h-5 mb-0.5"
            style={{ color: isMoreOpen ? 'var(--accent-bg)' : 'var(--sidebar-text)' }}
          />
          <span
            className="text-[10px] font-medium"
            style={{ color: isMoreOpen ? 'var(--accent-bg)' : 'var(--sidebar-text)' }}
          >
            More
          </span>
          {unreadCount > 0 && <div className="absolute top-0.5 right-1.5 w-2 h-2 bg-expense-500 rounded-full border border-[var(--sidebar-bg)]" />}
        </button>
      </div>
    </nav>
  );

  return (
    <>
      {/* Bottom Navigation Bar - Render based on style, hide when keyboard is open */}
      {!isKeyboardOpen && bottomNavStyle === 'classic' && renderClassicNav()}
      {!isKeyboardOpen && bottomNavStyle === 'pill' && renderPillNav()}
      {!isKeyboardOpen && bottomNavStyle === 'floating' && renderFloatingNav()}
      {!isKeyboardOpen && bottomNavStyle === 'floatingClassic' && renderFloatingClassicNav()}

      {/* More Menu — Bottom Sheet */}
      {isMoreOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[99] xl:hidden"
            onClick={closeMoreMenu}
          />
          <div
            data-more-menu
            className="fixed bottom-0 left-0 right-0 z-[100] xl:hidden rounded-t-3xl flex flex-col"
            style={{
              backgroundColor: isIOS ? '#f2f2f7' : '#f8fafc',
              maxHeight: '85vh',
              transform: `translateY(${menuDragY}px)`,
              transition: isMenuDragging ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
            }}
            onTouchStart={handleMenuTouchStart}
            onTouchMove={handleMenuTouchMove}
            onTouchEnd={handleMenuTouchEnd}
          >
            {/* iOS Header */}
            {isIOS ? (
              <>
                <div className="flex justify-center pt-3 pb-1">
                  <div className="w-9 h-1 bg-[#c7c7cc] rounded-full" />
                </div>
                <div className="px-5 pb-3 flex items-center justify-between">
                  <h1 className="text-[28px] font-extrabold text-black tracking-tight">More</h1>
                  <button
                    onClick={closeMoreMenu}
                    className="w-9 h-9 rounded-full bg-[#e5e5ea] flex items-center justify-center active:bg-[#d1d1d6]"
                  >
                    <X className="w-4 h-4 text-[#8e8e93]" />
                  </button>
                </div>
              </>
            ) : (
              /* Android Header — themed gradient */
              <div
                className="rounded-t-3xl"
                style={{ background: 'linear-gradient(135deg, var(--accent-bg), color-mix(in srgb, var(--accent-bg) 80%, black))' }}
              >
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-9 h-1 bg-white/30 rounded-full" />
                </div>
                <div className="px-5 pb-3 flex items-center justify-between">
                  <div>
                    <h1 className="text-[22px] font-extrabold text-white tracking-tight">More</h1>
                    <p className="text-xs text-white/70 mt-0.5">Settings & preferences</p>
                  </div>
                  <button
                    onClick={closeMoreMenu}
                    className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center active:bg-white/25"
                  >
                    <X className="w-4 h-4 text-white/90" />
                  </button>
                </div>

                {/* Android: User card inside header */}
                <div className="mx-4 mb-4 p-3.5 rounded-2xl bg-white/12 border border-white/10 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-base">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-bold text-white truncate">{user?.name || 'User'}</p>
                    <p className="text-[11px] text-white/60 truncate">{user?.email || ''}</p>
                  </div>
                  <button
                    onClick={() => handleMoreItemClick('/settings/profile')}
                    className="px-3 py-1.5 rounded-lg bg-white/20 border border-white/15 text-white text-xs font-semibold active:bg-white/30"
                  >
                    Edit
                  </button>
                </div>
              </div>
            )}

            {/* Scrollable Content */}
            <div
              ref={menuContentRef}
              className="flex-1 overflow-y-auto"
              style={{ paddingBottom: '24px' }}
            >
              {/* iOS: User profile card */}
              {isIOS && (
                <div className="mx-4 mb-4 bg-white rounded-2xl overflow-hidden">
                  <button
                    onClick={() => handleMoreItemClick('/settings/profile')}
                    className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-[#f2f2f7]"
                  >
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, var(--accent-bg), color-mix(in srgb, var(--accent-bg) 70%, black))' }}>
                      <span className="text-white font-bold text-lg">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-base font-bold text-black truncate">{user?.name || 'User'}</p>
                      <p className="text-xs text-[#8e8e93] truncate">{user?.email || ''}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#c7c7cc]" />
                  </button>
                </div>
              )}

              {/* Admin More Menu */}
              {user?.isAdmin ? (
                <>
                  <div className="px-4" style={{ paddingTop: isIOS ? '0' : '16px' }}>
                    {getAdminGroupedMenuItems().map((group) => (
                      <div key={group.id} className="mb-4">
                        <p className={`text-[11px] font-bold uppercase tracking-wider px-3 mb-1.5 ${isIOS ? 'text-[#8e8e93]' : 'font-extrabold'}`} style={!isIOS ? { color: 'var(--accent-bg)' } : {}}>
                          {group.label}
                        </p>
                        <div className={`rounded-2xl overflow-hidden ${isIOS ? 'bg-white' : 'bg-white border border-surface-100 shadow-sm'}`}>
                          {group.items.map((item, idx) => (
                            <button
                              key={item.path}
                              onClick={() => handleMoreItemClick(item.path)}
                              className={`w-full flex items-center gap-3 px-4 py-3 ${isIOS ? 'active:bg-[#f2f2f7]' : 'active:bg-surface-50'}`}
                              style={idx < group.items.length - 1 ? { borderBottom: isIOS ? '0.5px solid #e5e5ea' : '1px solid #f1f5f9' } : {}}
                            >
                              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                <span className="text-base">{item.emoji}</span>
                              </div>
                              <div className="flex-1 text-left">
                                <span className={`text-[15px] font-medium ${isIOS ? 'text-black' : 'text-surface-900'}`}>{item.label}</span>
                              </div>
                              <ChevronRight className={`w-4 h-4 ${isIOS ? 'text-[#c7c7cc]' : 'text-surface-300'}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
              {/* Quick Access Grid */}
              <div className="px-5 pb-4" style={{ paddingTop: isIOS ? '0' : '16px' }}>
                <div className="grid grid-cols-4 gap-3">
                  {getGroupedMenuItems().find(g => g.id === 'finance')?.items.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleMoreItemClick(item.path)}
                      className="flex flex-col items-center active:scale-95 transition-transform"
                    >
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-1"
                        style={{ background: 'color-mix(in srgb, var(--accent-bg) 15%, white)' }}
                      >
                        <span className="text-xl">{item.emoji}</span>
                      </div>
                      <span className={`text-[11px] font-medium ${isIOS ? 'text-[#3c3c43]' : 'text-surface-600'}`}>
                        {item.label}
                      </span>
                    </button>
                  ))}
                  {/* Trend in quick grid */}
                  <button
                    onClick={() => handleMoreItemClick('/trend')}
                    className="flex flex-col items-center active:scale-95 transition-transform"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center mb-1">
                      <span className="text-xl">📈</span>
                    </div>
                    <span className={`text-[11px] font-medium ${isIOS ? 'text-[#3c3c43]' : 'text-surface-600'}`}>
                      Trend
                    </span>
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className={`h-px mx-5 ${isIOS ? 'bg-[#e5e5ea]' : 'bg-surface-200'}`} />

              {/* List Sections */}
              <div className="px-4 pt-4">
                {/* Subscription */}
                {!hidePaymentUI && !freeAccessMode && (
                  <div className="mb-4">
                    <p className={`text-[11px] font-bold uppercase tracking-wider px-3 mb-1.5 font-extrabold`} style={{ color: 'var(--accent-bg)' }}>
                      Subscription
                    </p>
                    <div className={`rounded-2xl overflow-hidden ${isIOS ? 'bg-white' : 'bg-white border border-surface-100 shadow-sm'}`}>
                      <button
                        onClick={() => handleMoreItemClick(showSubscription ? '/pricing' : '/subscription')}
                        className={`w-full flex items-center gap-3 px-4 py-3 ${isIOS ? 'active:bg-[#f2f2f7]' : 'active:bg-surface-50'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${showSubscription ? 'bg-emerald-100' : 'bg-surface-100'}`}>
                          <span className="text-base">👑</span>
                        </div>
                        <div className="flex-1 text-left">
                          <span className={`text-[15px] font-medium ${isIOS ? 'text-black' : 'text-surface-900'}`}>
                            {showSubscription ? 'Subscribe' : 'Subscription'}
                          </span>
                          {!isIOS && <span className="block text-[11px] text-surface-400 mt-0.5">{showSubscription ? 'Upgrade your plan' : 'Manage plan'}</span>}
                        </div>
                        <ChevronRight className={`w-4 h-4 ${isIOS ? 'text-[#c7c7cc]' : 'text-surface-300'}`} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Finance & Tracking */}
                <div className="mb-4">
                  <p className={`text-[11px] font-bold uppercase tracking-wider px-3 mb-1.5 ${isIOS ? 'text-[#8e8e93]' : 'font-extrabold'}`} style={!isIOS ? { color: 'var(--accent-bg)' } : {}}>
                    Finance & Tracking
                  </p>
                  <div className={`rounded-2xl overflow-hidden ${isIOS ? 'bg-white' : 'bg-white border border-surface-100 shadow-sm'}`}>
                    {[
                      ...getGroupedMenuItems().find(g => g.id === 'tracking')?.items || [],
                    ].map((item, idx, arr) => (
                      <button
                        key={item.path}
                        onClick={() => handleMoreItemClick(item.path)}
                        className={`w-full flex items-center gap-3 px-4 py-3 ${isIOS ? 'active:bg-[#f2f2f7]' : 'active:bg-surface-50'}`}
                        style={idx < arr.length - 1 ? { borderBottom: isIOS ? '0.5px solid #e5e5ea' : '1px solid #f1f5f9' } : {}}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          item.label === 'Receivables' ? 'bg-green-100' :
                          item.label === 'Debts' ? 'bg-red-100' :
                          item.label === 'Checklist' ? 'bg-amber-100' : 'bg-indigo-100'
                        }`}>
                          <span className="text-base">{item.emoji}</span>
                        </div>
                        <div className="flex-1 text-left">
                          <span className={`text-[15px] font-medium ${isIOS ? 'text-black' : 'text-surface-900'}`}>{item.label}</span>
                          {!isIOS && item.label === 'Receivables' && <span className="block text-[11px] text-surface-400 mt-0.5">Money to receive</span>}
                          {!isIOS && item.label === 'Debts' && <span className="block text-[11px] text-surface-400 mt-0.5">Money to pay</span>}
                          {!isIOS && item.label === 'Checklist' && <span className="block text-[11px] text-surface-400 mt-0.5">Monthly bills & tasks</span>}
                          {!isIOS && item.label === 'Contacts' && <span className="block text-[11px] text-surface-400 mt-0.5">Manage contacts</span>}
                        </div>
                        <ChevronRight className={`w-4 h-4 ${isIOS ? 'text-[#c7c7cc]' : 'text-surface-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Analytics */}
                <div className="mb-4">
                  <p className={`text-[11px] font-bold uppercase tracking-wider px-3 mb-1.5 ${isIOS ? 'text-[#8e8e93]' : 'font-extrabold'}`} style={!isIOS ? { color: 'var(--accent-bg)' } : {}}>
                    Analytics
                  </p>
                  <div className={`rounded-2xl overflow-hidden ${isIOS ? 'bg-white' : 'bg-white border border-surface-100 shadow-sm'}`}>
                    {[
                      { path: '/ledger', emoji: '📖', label: 'Ledger', desc: 'Export transactions', color: 'bg-cyan-100' },
                      { path: '/insights', emoji: '✨', label: 'HJ Insights', desc: 'AI-powered insights', color: 'bg-violet-100' },
                      { path: '/categories', emoji: '🏷️', label: 'Categories', desc: 'Manage categories', color: 'bg-teal-100' },
                    ].map((item, idx, arr) => (
                      <button
                        key={item.path}
                        onClick={() => handleMoreItemClick(item.path)}
                        className={`w-full flex items-center gap-3 px-4 py-3 ${isIOS ? 'active:bg-[#f2f2f7]' : 'active:bg-surface-50'}`}
                        style={idx < arr.length - 1 ? { borderBottom: isIOS ? '0.5px solid #e5e5ea' : '1px solid #f1f5f9' } : {}}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}>
                          <span className="text-base">{item.emoji}</span>
                        </div>
                        <div className="flex-1 text-left">
                          <span className={`text-[15px] font-medium ${isIOS ? 'text-black' : 'text-surface-900'}`}>{item.label}</span>
                          {!isIOS && <span className="block text-[11px] text-surface-400 mt-0.5">{item.desc}</span>}
                        </div>
                        <ChevronRight className={`w-4 h-4 ${isIOS ? 'text-[#c7c7cc]' : 'text-surface-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
                </>
              )}

              {/* Shared sections: Settings, Support, Sign Out */}
              <div className="px-4 pt-4">
                {/* Preferences / Settings */}
                {!user?.isAdmin && (
                <div className="mb-4">
                  <p className={`text-[11px] font-bold uppercase tracking-wider px-3 mb-1.5 ${isIOS ? 'text-[#8e8e93]' : 'font-extrabold'}`} style={!isIOS ? { color: 'var(--accent-bg)' } : {}}>
                    Preferences
                  </p>
                  <div className={`rounded-2xl overflow-hidden ${isIOS ? 'bg-white' : 'bg-white border border-surface-100 shadow-sm'}`}>
                    {settingsMenuItems.map((item, idx) => {
                      const colors = SETTINGS_COLORS[item.color];
                      return (
                        <button
                          key={`settings-${item.path}`}
                          onClick={() => handleMoreItemClick(item.path)}
                          className={`w-full flex items-center gap-3 px-4 py-3 ${isIOS ? 'active:bg-[#f2f2f7]' : 'active:bg-surface-50'}`}
                          style={idx < settingsMenuItems.length - 1 ? { borderBottom: isIOS ? '0.5px solid #e5e5ea' : '1px solid #f1f5f9' } : {}}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.classicIconBox}`}>
                            <span className="text-base">{item.emoji}</span>
                          </div>
                          <div className="flex-1 text-left">
                            <span className={`text-[15px] font-medium ${isIOS ? 'text-black' : 'text-surface-900'}`}>{item.label}</span>
                            {!isIOS && <span className="block text-[11px] text-surface-400 mt-0.5">{item.description}</span>}
                          </div>
                          <ChevronRight className={`w-4 h-4 ${isIOS ? 'text-[#c7c7cc]' : 'text-surface-300'}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Support */}
                <div className="mb-4">
                  <p className={`text-[11px] font-bold uppercase tracking-wider px-3 mb-1.5 ${isIOS ? 'text-[#8e8e93]' : 'font-extrabold'}`} style={!isIOS ? { color: 'var(--accent-bg)' } : {}}>
                    Support
                  </p>
                  <div className={`rounded-2xl overflow-hidden ${isIOS ? 'bg-white' : 'bg-white border border-surface-100 shadow-sm'}`}>
                    {[
                      { action: openFeedbackModal, emoji: '💬', label: 'Send Feedback', desc: 'Help us improve', color: 'bg-primary-100' },
                      ...(!user?.isAdmin ? [{ action: () => handleMoreItemClick('/passcode'), emoji: '🔒', label: 'App Lock', desc: 'Passcode protection', color: 'bg-amber-100' }] : []),
                      ...(!hidePaymentUI && !isIOS && !(/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && referralEnabled && !user?.isAdmin ? [{
                        action: () => handleMoreItemClick('/referrals'), emoji: '🎁', label: 'Referrals', desc: 'Invite & earn', color: 'bg-violet-100'
                      }] : []),
                      ...(isNative && !isIOS ? [{
                        action: () => window.open('https://app.hisabjourney.com/', '_blank'), emoji: '🌐', label: 'Web Access', desc: 'Open in browser', color: 'bg-cyan-100'
                      }] : []),
                      ...(canInstall ? [{
                        action: installPWA, emoji: '📲', label: 'Install App', desc: 'Add to home screen', color: 'bg-income-100'
                      }] : []),
                    ].map((item, idx, arr) => (
                      <button
                        key={item.label}
                        onClick={item.action}
                        className={`w-full flex items-center gap-3 px-4 py-3 ${isIOS ? 'active:bg-[#f2f2f7]' : 'active:bg-surface-50'}`}
                        style={idx < arr.length - 1 ? { borderBottom: isIOS ? '0.5px solid #e5e5ea' : '1px solid #f1f5f9' } : {}}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}>
                          <span className="text-base">{item.emoji}</span>
                        </div>
                        <div className="flex-1 text-left">
                          <span className={`text-[15px] font-medium ${isIOS ? 'text-black' : 'text-surface-900'}`}>{item.label}</span>
                          {!isIOS && <span className="block text-[11px] text-surface-400 mt-0.5">{item.desc}</span>}
                        </div>
                        <ChevronRight className={`w-4 h-4 ${isIOS ? 'text-[#c7c7cc]' : 'text-surface-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sign Out */}
                <div className={`rounded-2xl overflow-hidden mb-4 ${isIOS ? 'bg-white' : 'bg-white border border-expense-100 shadow-sm'}`}>
                  <button
                    onClick={handleLogout}
                    className={`w-full flex items-center justify-center gap-2 py-3.5 ${isIOS ? 'active:bg-[#f2f2f7]' : 'active:bg-expense-50'}`}
                  >
                    {!isIOS && <span className="text-sm">🚪</span>}
                    <span className={`text-[15px] font-semibold ${isIOS ? 'text-[#ff3b30]' : 'text-expense-600'}`}>Sign Out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div
          className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[110]"
          onClick={closeFeedbackModal}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle (mobile) */}
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {feedbackSuccess ? (
              <div className="text-center py-12 px-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#dcfce7' }}>
                  <Send className="w-7 h-7" style={{ color: '#16a34a' }} />
                </div>
                <h4 className="text-lg font-bold text-surface-900 mb-1">Thank you!</h4>
                <p className="text-[13px] text-surface-400">Your feedback has been submitted. We'll get back to you soon.</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between px-4 pt-2 pb-3 sm:pt-4">
                  <div>
                    <h3 className="text-[15px] font-bold text-surface-900">Send Feedback</h3>
                    <p className="text-[11px] text-surface-400 mt-0.5">Help us improve the app</p>
                  </div>
                  <button
                    type="button"
                    onClick={closeFeedbackModal}
                    className="p-2 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-4 pb-4 space-y-4">
                  {/* Feedback type pills */}
                  <div>
                    <p className="text-[12px] font-semibold text-surface-500 mb-2">What's this about?</p>
                    <div className="flex gap-2">
                      {[
                        { key: 'bug', label: 'Bug Report', emoji: '🐛' },
                        { key: 'feature', label: 'Feature', emoji: '💡' },
                        { key: 'feedback', label: 'General', emoji: '🙏' }
                      ].map(t => (
                        <button
                          key={t.key}
                          onClick={() => setFeedbackType(t.key)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all border ${
                            feedbackType === t.key
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-surface-200 bg-white text-surface-500'
                          }`}
                        >
                          <span>{t.emoji}</span>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {feedbackError && (
                    <div className="p-2.5 rounded-xl text-[12px] font-medium" style={{ background: '#fef2f2', color: '#ef4444' }}>
                      {feedbackError}
                    </div>
                  )}

                  {/* Subject */}
                  <div>
                    <label className="block text-[12px] font-semibold text-surface-500 mb-1.5">Subject</label>
                    <input
                      type="text"
                      value={feedbackTitle}
                      onChange={(e) => setFeedbackTitle(e.target.value)}
                      placeholder="Brief summary of your feedback"
                      className="w-full px-3 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-[13px] text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      maxLength={200}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-[12px] font-semibold text-surface-500 mb-1.5">Description</label>
                    <textarea
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder="Tell us more details..."
                      rows={4}
                      className="w-full px-3 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-[13px] text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      maxLength={2000}
                    />
                    <p className="text-[10px] text-surface-400 mt-1 text-right">{feedbackMessage.length}/2000</p>
                  </div>

                  {/* Email info */}
                  <div className="flex items-start gap-2 p-2.5 rounded-xl" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <span className="text-sm flex-shrink-0">ℹ️</span>
                    <span className="text-[11px] leading-relaxed" style={{ color: '#166534' }}>
                      Your email ({user?.email}) will be included so we can respond.
                    </span>
                  </div>

                  {/* Submit */}
                  <button
                    type="button"
                    onClick={handleFeedbackSubmit}
                    disabled={feedbackSubmitting || !feedbackTitle.trim() || !feedbackMessage.trim()}
                    className="w-full py-3 rounded-xl font-semibold text-[13px] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                  >
                    {feedbackSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />Sending...</>
                    ) : (
                      <><Send className="w-4 h-4" />Send Feedback</>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
